// Finance Books — Payments (Customer Receipts + Vendor Payments).
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import fb from '../api';
import {
  PageHeader, Btn, Drawer, Input, Select, Textarea, Table, Pill,
  fmtMoney, fmtDate, todayISO, SOFT,
} from '../ui';

function PaymentForm({ open, onClose, kind, onSaved }) {
  // kind: 'received' (customers/invoices) or 'vendor' (vendors/bills)
  const partyKind = kind === 'received' ? 'customer' : 'vendor';
  const docLabel = kind === 'received' ? 'Invoice' : 'Bill';
  const savePath = kind === 'received' ? '/payments-received' : '/vendor-payments';
  const partyPath = kind === 'received' ? '/customers' : '/vendors';
  const docsPath = kind === 'received' ? '/invoices' : '/bills';
  const partyIdField = partyKind === 'customer' ? 'customer_id' : 'vendor_id';
  const docIdField = kind === 'received' ? 'invoice_id' : 'bill_id';

  const [parties, setParties] = useState([]);
  const [banks, setBanks] = useState([]);
  const [openDocs, setOpenDocs] = useState([]);
  const [form, setForm] = useState({
    [partyIdField]: '', date: todayISO(), amount: 0, bank_account_id: '', mode: 'bank',
    reference: '', notes: '', allocations: [],
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      setParties(await fb.get(partyPath));
      const accs = await fb.get('/accounts?type=asset');
      setBanks((accs || []).filter((a) => a.is_bank));
    })();
  }, [open, partyPath]);

  useEffect(() => {
    if (!form[partyIdField]) { setOpenDocs([]); return; }
    (async () => {
      const docs = await fb.get(docsPath, { [partyIdField]: form[partyIdField] });
      setOpenDocs((docs || []).filter((d) => d.status !== 'paid' && d.status !== 'void'));
    })();
  }, [form[partyIdField], partyIdField, docsPath]);

  const setAlloc = (docId, amt) => {
    const others = form.allocations.filter((a) => a[docIdField] !== docId);
    const v = Number(amt) || 0;
    setForm({ ...form, allocations: v > 0 ? [...others, { [docIdField]: docId, amount: v }] : others });
  };
  const allocTotal = form.allocations.reduce((s, a) => s + Number(a.amount || 0), 0);

  const save = async () => {
    try {
      if (!form[partyIdField]) return toast.error(`${partyKind} required`);
      if (!form.bank_account_id) return toast.error('Bank account required');
      if (!form.amount) return toast.error('Amount required');
      const payload = { ...form, amount: Number(form.amount) };
      await fb.post(savePath, payload);
      toast.success('Payment recorded · auto-journaled');
      onSaved?.(); onClose();
      setForm({ [partyIdField]: '', date: todayISO(), amount: 0, bank_account_id: '', mode: 'bank', reference: '', notes: '', allocations: [] });
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`Record ${kind === 'received' ? 'Customer Receipt' : 'Vendor Payment'}`} width="max-w-3xl">
      <div className="grid grid-cols-3 gap-3">
        <Select label={`${partyKind} *`} value={form[partyIdField]} onChange={(e) => setForm({ ...form, [partyIdField]: e.target.value, allocations: [] })} data-testid="pay-party">
          <option value="">— Select —</option>
          {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <Input label="Amount *" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" />
        <Select label="Bank Account *" value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} data-testid="pay-bank">
          <option value="">— Select —</option>
          {banks.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
        </Select>
        <Select label="Mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
          <option value="bank">Bank Transfer</option>
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="cheque">Cheque</option>
          <option value="card">Card</option>
        </Select>
        <Input label="Reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
      </div>

      {form[partyIdField] && (
        <div className="mt-5">
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Allocate to Open {docLabel}s</div>
          {!openDocs.length ? (
            <p className="text-white/40 text-sm italic">No open {docLabel.toLowerCase()}s. The remaining amount will be saved as an advance.</p>
          ) : (
            <div className={`${SOFT} rounded-lg overflow-hidden`}>
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-white/55 text-[10px] tracking-[0.2em] uppercase">
                  <tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2 text-right">Apply</th></tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {openDocs.map((d) => {
                    const a = form.allocations.find((x) => x[docIdField] === d.id);
                    return (
                      <tr key={d.id}>
                        <td className="px-3 py-2 font-mono text-xs text-white/80">{d.doc_no}</td>
                        <td className="px-3 py-2">{fmtDate(d.date)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(d.grand_total)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-orange-300">{fmtMoney(d.balance_due)}</td>
                        <td className="px-3 py-2 text-right">
                          <input
                            className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs w-28 text-right tabular-nums text-white"
                            type="number" placeholder="0"
                            value={a?.amount || ''}
                            onChange={(e) => setAlloc(d.id, e.target.value)}
                            data-testid={`alloc-${d.doc_no}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-2 text-xs text-white/55">
            Allocated: <span className="text-white tabular-nums">{fmtMoney(allocTotal)}</span> · Advance: <span className="text-orange-300 tabular-nums">{fmtMoney((Number(form.amount) || 0) - allocTotal)}</span>
          </div>
        </div>
      )}

      <Textarea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      <div className="flex justify-end gap-2 mt-5">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="accent" onClick={save} data-testid="pay-save">Save Payment</Btn>
      </div>
    </Drawer>
  );
}

export function PaymentsReceived() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const reload = async () => setRows(await fb.get('/payments-received'));
  useEffect(() => { reload(); }, []);
  return (
    <div data-testid="fb-payments-received">
      <PageHeader title="Payments Received" subtitle={`${rows.length} receipts · double-entry posted automatically`}
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="fb-new-receipt">+ Record Payment</Btn>} />
      <Table
        columns={[
          { key: 'pay_no', label: '#', render: (r) => <span className="font-mono text-xs text-white/80">{r.pay_no}</span> },
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
          { key: 'customer_name', label: 'Customer' },
          { key: 'mode', label: 'Mode', render: (r) => <Pill tone="blue">{r.mode}</Pill> },
          { key: 'amount', label: 'Amount', align: 'right', render: (r) => <span className="tabular-nums text-emerald-300">{fmtMoney(r.amount)}</span> },
          { key: 'advance', label: 'Advance', align: 'right', render: (r) => r.advance > 0 ? <span className="tabular-nums text-orange-300">{fmtMoney(r.advance)}</span> : <span className="text-white/30">—</span> },
        ]}
        rows={rows} empty="No receipts yet."
      />
      <PaymentForm open={open} onClose={() => setOpen(false)} kind="received" onSaved={reload} />
    </div>
  );
}

export function VendorPayments() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const reload = async () => setRows(await fb.get('/vendor-payments'));
  useEffect(() => { reload(); }, []);
  return (
    <div data-testid="fb-vendor-payments">
      <PageHeader title="Vendor Payments" subtitle={`${rows.length} payments · double-entry posted automatically`}
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="fb-new-vpay">+ Pay Vendor</Btn>} />
      <Table
        columns={[
          { key: 'pay_no', label: '#', render: (r) => <span className="font-mono text-xs text-white/80">{r.pay_no}</span> },
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
          { key: 'vendor_name', label: 'Vendor' },
          { key: 'mode', label: 'Mode', render: (r) => <Pill tone="blue">{r.mode}</Pill> },
          { key: 'amount', label: 'Amount', align: 'right', render: (r) => <span className="tabular-nums text-rose-300">{fmtMoney(r.amount)}</span> },
          { key: 'advance', label: 'Advance', align: 'right', render: (r) => r.advance > 0 ? <span className="tabular-nums text-orange-300">{fmtMoney(r.advance)}</span> : <span className="text-white/30">—</span> },
        ]}
        rows={rows} empty="No vendor payments yet."
      />
      <PaymentForm open={open} onClose={() => setOpen(false)} kind="vendor" onSaved={reload} />
    </div>
  );
}
